import { Separator } from "../../../components/separator";
import Documents from "../sections/documents";
import Message from "../sections/message";
import { shared_documents_list } from "../../../utils/shared/documents-list";
import { FileItem } from "../../../models/polymer/Document.model";
import {
  StageDMessageInfo,
  StageDMessageType,
  StageDReviewApplication,
  StageDSolicitorApplication,
  StageDocumentsInfo
} from "../../../models/classes/stage-d/Review-Application.class";
import HdbCannotActForm from "../segments/hdb-cannot-act-form";
import { SolicitorTypeEnum } from "../../../models/enum/solicitor-type.enum";
import { useCallback, useMemo } from "react";
import { cloneDeep } from "lodash";

interface ApplicationUpdateFormProps {
  setFileItemsStorage?: Function;
  filesItemsStorage: FileItem[];
  pendingItems: string[];
  applicationReviewValues: StageDReviewApplication;
  applicationReviewSetFormValue: (value: any) => void;
  messageType: StageDMessageType;
  solicitorMessageInfo: StageDMessageInfo[] | undefined;
  currentSolicitorFormValue: (value: StageDSolicitorApplication[]) => void;
  currentSolicitorErrors: any;
  currentSolicitorValues: StageDSolicitorApplication[];
  proposedSolicitorFormValue: (value: StageDSolicitorApplication[]) => void;
  proposedSolicitorErrors: any;
  proposedSolicitorValues: StageDSolicitorApplication[];
  normalFolders: StageDocumentsInfo[];
  solicitorFolders: StageDocumentsInfo[];
}

function ApplicationUpdateForm({
  messageType = "GENERIC",
  setFileItemsStorage,
  filesItemsStorage,
  applicationReviewValues,
  pendingItems,
  applicationReviewSetFormValue,
  normalFolders,
  solicitorFolders,
  ...props
}: ApplicationUpdateFormProps) {
  let firstItem: string = "";
  if (pendingItems.length > 0) {
    firstItem = pendingItems[0];
  }

  const HDBFormHandlerForCurrentOrOutgoing = useCallback(
    (info: StageDMessageInfo) => (value: StageDSolicitorApplication) => {
      let existing_value = cloneDeep(props.currentSolicitorValues);
      const ownerType = info.owner_type === "CURRENT_OWNER" ? "current" : "outgoing";
      
      const processedValue = cloneDeep(value);
      if(processedValue[ownerType].private_solicitor.represented_same_solicitor){
         processedValue[ownerType].conveyancing_private_solicitor = cloneDeep(processedValue[ownerType].private_solicitor);
      }
      let stageDObj = new StageDSolicitorApplication({ ...processedValue, uuid: info.uuid, group_uuid: info.group_uuid });
      let foundIndex = existing_value?.findIndex((e) => e.uuid === info.uuid);
      if (foundIndex === -1) {
        existing_value.push(stageDObj);
      } else {
        existing_value[foundIndex] = stageDObj;
      }
      props.currentSolicitorFormValue(existing_value);
    },
    [props]
  );

  const HDBFormHandlerForProposed = useCallback(
    (info: StageDMessageInfo) => (value: StageDSolicitorApplication) => {
      let existing_value = cloneDeep(props.proposedSolicitorValues);
      const processedValue = cloneDeep(value);
      if(processedValue.proposed.private_solicitor.represented_same_solicitor){
         processedValue.proposed.conveyancing_private_solicitor = cloneDeep(processedValue.proposed.private_solicitor);
      }
      let stageDObj = new StageDSolicitorApplication({ ...processedValue, uuid: info.uuid, group_uuid: info.group_uuid });
      let foundIndex = existing_value?.findIndex((e) => e.uuid === info.uuid);
      if (foundIndex === -1) {
        existing_value.push(stageDObj);
      } else {
        existing_value[foundIndex] = stageDObj;
      }
      props.proposedSolicitorFormValue(existing_value);
    },
    [props]
  );

  const hasMessages = useMemo(() => {
    return applicationReviewValues?.message_info?.length > 0;
  }, [applicationReviewValues]);

  return (
    <div className="polymer-stage-d-container">
      {messageType === "SOLICITOR" && (
        <>
          {props.solicitorMessageInfo?.map((info: StageDMessageInfo, _index: number) => {
            const key = `hdb-cannot-act-${info.uuid}`;
            const ownerType = info.owner_type === "CURRENT_OWNER" ? "current" : "outgoing"
            return info.owner_type === "CURRENT_OWNER" || info.owner_type === "OUTGOING_OWNER" ? (
              <div key={key}>
                <HdbCannotActForm
                  solicitorMessageInfo={info}
                  displayChildOnly
                  legalRepresentation={props.currentSolicitorValues[0]?.[ownerType]}
                  solicitor_type={SolicitorTypeEnum.private_solicitor}
                  ConveyancingSetFormValue={HDBFormHandlerForCurrentOrOutgoing(info)}
                  ConveyancingErrors={props.currentSolicitorErrors[0]?.[ownerType]}
                />
                {props.solicitorMessageInfo?.length! > _index + 1 && <Separator variant="solid" style={{ margin: "32px 0 24px" }} />}
              </div>
            ) : (
              <div key={key}>
                <HdbCannotActForm
                  solicitorMessageInfo={info}
                  displayChildOnly
                  legalRepresentation={props.proposedSolicitorValues[0]?.proposed}
                  solicitor_type={SolicitorTypeEnum.private_solicitor}
                  ConveyancingSetFormValue={HDBFormHandlerForProposed(info)}
                  ConveyancingErrors={props.proposedSolicitorErrors[0]?.proposed}
                />
                {props.solicitorMessageInfo?.length! > _index + 1 && <Separator variant="solid" style={{ margin: "32px 0 24px" }} />}
              </div>
            );
          })}
        </>
      )}
      {messageType === "SOLICITOR" && solicitorFolders.length > 0 && 
        <>
          <Separator variant="solid" style={{ margin: "32px 0 0" }} />
          <Documents
            folders={solicitorFolders}
            applicationReviewSetFormValue={applicationReviewSetFormValue}
            document_list={shared_documents_list}
            filesItemsStorage={filesItemsStorage}
            setFileItemsStorage={setFileItemsStorage}
            isFirstSection={false}
          />
          {hasMessages && <Separator variant="solid" style={{ margin: "32px 0 24px" }} />}
        </>
      }
      {pendingItems.includes("messages") && <Message value={applicationReviewValues} setFormValue={applicationReviewSetFormValue} />}
      {pendingItems.includes("messages") && pendingItems.includes("documents") && <Separator variant="solid" />}
      {pendingItems.includes("documents") && normalFolders.length > 0 && (
        <Documents
          folders={normalFolders}
          applicationReviewSetFormValue={applicationReviewSetFormValue}
          document_list={shared_documents_list}
          filesItemsStorage={filesItemsStorage}
          setFileItemsStorage={setFileItemsStorage}
          isFirstSection={firstItem === "documents"}
        />
      )}
    </div>
  );
}

export default ApplicationUpdateForm;
